import { makeStyles } from "@material-ui/core/styles";
import DOMPurify from "dompurify";
import smallRobotAvatar from "../images/robotSmall.svg";

const useStyles = makeStyles({
	row: {
		display: "flex",
		alignItems: "flex-end",
		justifyContent: "flex-start",
		textAlign: "left",
		marginLeft: "8px",
		marginBottom: "8px",
		width: "100%",
	},
	avatar: {
		width: "30px",
		height: "30px",
		marginRight: "12px",
		borderRadius: "50%",
		objectFit: "cover",
		alignSelf: "flex-end",
		flexShrink: 0,
	},
	body: {
		padding: "12px 16px",
		backgroundColor: "#fff",
		color: "var(--bot-msg-txt)",
		display: "inline-block",
		borderRadius: "18px 18px 18px 6px",
		marginRight: "48px",
		textAlign: "left",
		overflowWrap: "break-word",
		maxWidth: "min(78%, 540px)",
		border: "1px solid var(--surface-border)",
		borderLeft: "3px solid var(--active-brand-color)",
		boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
		whiteSpace: "pre-wrap",
		lineHeight: 1.5,
		fontFamily: "var(--font-sans)",
		letterSpacing: "-0.01em",
		"& p": { margin: "0 0 0.5em 0" },
		"& p:last-child": { marginBottom: 0 },
		"& ul, & ol": { margin: "0.35em 0", paddingLeft: "1.25em" },
	},
	cursor: {
		display: "inline-block",
		width: "2px",
		height: "1em",
		marginLeft: "2px",
		backgroundColor: "currentColor",
		verticalAlign: "text-bottom",
		opacity: 0.7,
		animation: "$blink 1s steps(1) infinite",
	},
	"@keyframes blink": {
		"0%, 50%": { opacity: 0.7 },
		"50.01%, 100%": { opacity: 0 },
	},
});

/**
 * Lightweight markdown → HTML for live stream preview.
 * Hides incomplete trailing markers and never shows raw HTML tags.
 */
export function formatStreamingMarkdown(raw) {
	if (!raw) return "";

	let text = String(raw)
		// Strip any HTML the model may emit mid-stream
		.replace(/<[^>]*>/g, "")
		// Drop incomplete trailing markdown tokens
		.replace(/(\*\*|__|`|~~|#+\s*)$/g, "")
		.replace(/\n?CITED_DOCS:\s*\[[^\]]*$/i, "")
		.replace(/\n?CITED_DOCS:\s*\[[^\]]*\]\s*$/i, "");

	// Escape HTML entities before adding our own tags
	text = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

	// Bold (complete pairs only) — avoid incomplete ** mid-stream
	text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
	text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");

	// Simple headings
	text = text.replace(/^### (.+)$/gm, "<strong>$1</strong>");
	text = text.replace(/^## (.+)$/gm, "<strong>$1</strong>");
	text = text.replace(/^# (.+)$/gm, "<strong>$1</strong>");

	// Paragraph breaks
	text = text.replace(/\n{2,}/g, "</p><p>");
	text = text.replace(/\n/g, "<br/>");
	text = `<p>${text}</p>`;

	return DOMPurify.sanitize(text, { ALLOWED_TAGS: ["p", "br", "strong", "em", "ul", "ol", "li"] });
}

export default function StreamingMessageBubble({ text, avatarUrl }) {
	const classes = useStyles();
	const html = formatStreamingMarkdown(text);

	return (
		<div className={classes.row} aria-label="Streaming chatbot message" role="article">
			{avatarUrl ? (
				<img src={avatarUrl} alt="" className={classes.avatar} onError={(e) => { e.target.src = smallRobotAvatar; }} />
			) : (
				<img className={classes.avatar} src={smallRobotAvatar} alt="" />
			)}
			<div className={classes.body}>
				<span dangerouslySetInnerHTML={{ __html: html }} />
				<span className={classes.cursor} aria-hidden="true" />
			</div>
		</div>
	);
}
